import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import JobCard from '../components/JobCard';
import Seo from '../components/Seo';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import LocationAutocomplete from '../components/LocationAutocomplete';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { API_URL, jobTypeOptions } from '../lib/utils';
import { TECH_ADMIN_FILTERS } from '../lib/techAdminSupport';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Search, MapPin, Filter, X, Briefcase, Loader2, BookOpen, ExternalLink, CheckCircle2, BadgeCheck } from 'lucide-react';
import SaveSearchButton from '../components/SaveSearchButton';

// Social work images for the page
const JOBS_PAGE_IMAGES = [
  "https://customer-assets.emergentagent.com/job_9b86f885-0f5e-479a-98ce-1634cbd87cc9/artifacts/wl78zpw2_ChatGPT%20Image%20Feb%2020%2C%202026%2C%2012_32_03%20PM.png",
  "https://customer-assets.emergentagent.com/job_9b86f885-0f5e-479a-98ce-1634cbd87cc9/artifacts/1qdb25hl_ChatGPT%20Image%20Feb%2020%2C%202026%2C%2001_31_34%20PM.png",
  "https://customer-assets.emergentagent.com/job_9b86f885-0f5e-479a-98ce-1634cbd87cc9/artifacts/77ewh4rh_ChatGPT%20Image%20Feb%2020%2C%202026%2C%2001_33_40%20PM.png"
];

const BOOK_IMAGE = "https://customer-assets.emergentagent.com/job_70b10f6b-9cd0-4369-aba2-66c0c28ea5fe/artifacts/nxmwrw6l_ChatGPT%20Image%20Feb%2020%2C%202026%2C%2005_57_23%20AM.png";
const AMAZON_BOOK_LINK = "https://a.co/d/0f4Uagyp";

// Special Populations & Skills — keep labels in sync with backend POPULATIONS_SERVED
const POPULATIONS = [
  { slug: 'deaf-hoh', label: 'Deaf & Hard of Hearing / ASL' },
  { slug: 'veterans', label: 'Veterans & Military' },
  { slug: 'autism-idd', label: 'Autism / IDD' },
  { slug: 'blind-low-vision', label: 'Blind / Low Vision' },
  { slug: 'lgbtq', label: 'LGBTQ+' },
  { slug: 'older-adults', label: 'Older Adults & Aging' },
  { slug: 'children-families', label: 'Children & Families' },
  { slug: 'homelessness', label: 'Homelessness & Housing' },
  { slug: 'substance-use', label: 'Substance Use & Recovery' },
  { slug: 'justice-involved', label: 'Justice-Involved' },
  { slug: 'foster-adoption', label: 'Foster Care & Adoption' },
];

// Track Amazon book click
const trackBookClick = (referrerPage) => {
  try {
    axios.post(`${API_URL}/analytics/book-click`, { referrer_page: referrerPage });
  } catch (error) { console.error('Book click tracking failed:', error); }
};

const Jobs = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savedJobs, setSavedJobs] = useState(new Set());
  const [showFilters, setShowFilters] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  
  // Filter state
  const [filters, setFilters] = useState({
    search: searchParams.get('search') || '',
    location: searchParams.get('location') || '',
    license: searchParams.get('license') || 'all',
    jobType: searchParams.get('job_type') || 'all',
    isRemote: searchParams.get('is_remote') === 'true',
    isGovernment: searchParams.get('is_government') === 'true',
    settingType: searchParams.get('setting') || 'all',
    supervisionProvided: searchParams.get('supervision') === 'true',
    populations: searchParams.get('populations') ? searchParams.get('populations').split(',').filter(Boolean) : [],
    support: searchParams.get('support') ? searchParams.get('support').split(',').filter(Boolean) : [],
  });

  useEffect(() => {
    fetchJobs();
    if (isAuthenticated) {
      fetchSavedJobs();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const fetchJobs = async (attempt = 0) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      
      if (searchParams.get('search')) params.append('search', searchParams.get('search'));
      if (searchParams.get('location')) params.append('location', searchParams.get('location'));
      if (searchParams.get('license') && searchParams.get('license') !== 'all') {
        params.append('license_type', searchParams.get('license'));
      }
      if (searchParams.get('job_type')) params.append('job_type', searchParams.get('job_type'));
      if (searchParams.get('is_remote') === 'true') params.append('is_remote', 'true');
      if (searchParams.get('is_government') === 'true') params.append('is_government', 'true');
      if (searchParams.get('populations')) params.append('populations', searchParams.get('populations'));
      if (searchParams.get('support')) params.append('support', searchParams.get('support'));
      params.append('page', searchParams.get('page') || '1');
      
      const response = await axios.get(`${API_URL}/jobs?${params.toString()}`);
      setJobs(response.data.jobs);
      setPagination({
        page: response.data.page,
        pages: response.data.pages,
        total: response.data.total,
      });
      setLoading(false);
    } catch (error) {
      console.error('Error fetching jobs:', error);
      // Retry transient failures (e.g. brief backend restart / 502) before giving up
      if (attempt < 2) {
        setTimeout(() => fetchJobs(attempt + 1), 1500);
        return;
      }
      toast.error('Failed to load jobs. Please refresh to try again.');
      setLoading(false);
    }
  };

  const fetchSavedJobs = async () => {
    try {
      const response = await axios.get(`${API_URL}/saved-jobs`, {
        headers: {  },
      });
      setSavedJobs(new Set(response.data.map(job => job.job_id)));
    } catch (error) {
      console.error('Error fetching saved jobs:', error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    
    if (filters.search) params.set('search', filters.search);
    if (filters.location) params.set('location', filters.location);
    if (filters.license && filters.license !== 'all') params.set('license', filters.license);
    if (filters.jobType && filters.jobType !== 'all') params.set('job_type', filters.jobType);
    if (filters.isRemote) params.set('is_remote', 'true');
    if (filters.isGovernment) params.set('is_government', 'true');
    if (filters.populations?.length) params.set('populations', filters.populations.join(','));
    
    setSearchParams(params);
  };

  const togglePopulation = (slug) => {
    const current = searchParams.get('populations') ? searchParams.get('populations').split(',').filter(Boolean) : [];
    const next = current.includes(slug) ? current.filter((s) => s !== slug) : [...current, slug];
    const params = new URLSearchParams(searchParams);
    if (next.length) params.set('populations', next.join(','));
    else params.delete('populations');
    params.delete('page');
    setFilters((f) => ({ ...f, populations: next }));
    setSearchParams(params);
  };

  const toggleSupport = (val) => {
    const current = searchParams.get('support') ? searchParams.get('support').split(',').filter(Boolean) : [];
    const next = current.includes(val) ? current.filter((s) => s !== val) : [...current, val];
    const params = new URLSearchParams(searchParams);
    if (next.length) params.set('support', next.join(','));
    else params.delete('support');
    params.delete('page');
    setFilters((f) => ({ ...f, support: next }));
    setSearchParams(params);
  };

  const toggleQuickFilter = (paramKey, filterKey) => {
    const params = new URLSearchParams(searchParams);
    const isOn = params.get(paramKey) === 'true';
    if (isOn) {
      params.delete(paramKey);
    } else {
      params.set(paramKey, 'true');
    }
    params.delete('page');
    setSearchParams(params);
    setFilters((f) => ({ ...f, [filterKey]: !isOn }));
  };

  const handleSaveJob = async (jobId) => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    
    try {
      await axios.post(`${API_URL}/saved-jobs/${jobId}`, {}, {
        headers: {  },
      });
      setSavedJobs(prev => new Set([...prev, jobId]));
      toast.success('Job saved!');
    } catch (error) {
      if (error.response?.status === 400) {
        toast.info('Job already saved');
      } else {
        toast.error('Failed to save job');
      }
    }
  };

  const handleUnsaveJob = async (jobId) => {
    try {
      await axios.delete(`${API_URL}/saved-jobs/${jobId}`, {
        headers: {  },
      });
      setSavedJobs(prev => {
        const newSet = new Set(prev);
        newSet.delete(jobId);
        return newSet;
      });
      toast.success('Job removed from saved');
    } catch (error) {
      toast.error('Failed to remove job');
    }
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      location: '',
      license: 'all',
      jobType: 'all',
      isRemote: false,
      isGovernment: false,
      settingType: 'all',
      supervisionProvided: false,
      populations: [],
    });
    setSearchParams(new URLSearchParams());
  };

  const hasActiveFilters = filters.search || filters.location || 
    (filters.license && filters.license !== 'all') || (filters.jobType && filters.jobType !== 'all') || 
    filters.isRemote || filters.isGovernment || (filters.populations && filters.populations.length > 0);

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      <Seo
        exactTitle="Social Work Jobs Nationwide | LBSW, LMSW & LCSW Jobs | INASW"
        description="Search social work jobs nationwide — LBSW, LMSW and LCSW roles in hospitals, hospice, schools, behavioral health, remote and PRN. New jobs posted daily on I Need A Social Worker."
        path="/jobs"
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6">
          <p className="text-secondary text-sm font-medium mb-1">Created Exclusively for Social Workers</p>
          <h1 className="font-serif text-3xl sm:text-4xl font-bold text-gray-900 mb-2">
            {pagination.total.toLocaleString()} Jobs Posted
          </h1>
          <p className="text-gray-600">
            Social work positions available for LBSW, LMSW, LCSW, and SWA professionals
          </p>
          <p className="text-sm text-primary mt-1">
            New jobs posted daily at 8:00 AM CST
          </p>
          {/* Quick filter chips */}
          <div className="flex flex-wrap items-center gap-2 mt-4" data-testid="quick-filter-chips">
            <button
              type="button"
              onClick={() => {
                const params = new URLSearchParams(searchParams);
                params.delete('is_remote');
                params.delete('is_government');
                params.delete('page');
                setSearchParams(params);
                setFilters((f) => ({ ...f, isRemote: false, isGovernment: false }));
              }}
              data-testid="quick-chip-all"
              className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors ${
                (searchParams.get('is_remote') !== 'true' && searchParams.get('is_government') !== 'true')
                  ? 'border-primary bg-primary text-white'
                  : 'border-gray-200 bg-white text-gray-600 hover:border-primary/50 hover:text-primary'
              }`}
            >
              All Social Work Jobs
            </button>
            {[
              { param: 'is_remote', fkey: 'isRemote', label: 'Remote' },
              { param: 'is_government', fkey: 'isGovernment', label: 'Government' },
            ].map(({ param, fkey, label }) => {
              const active = searchParams.get(param) === 'true';
              return (
                <button
                  key={param}
                  type="button"
                  onClick={() => toggleQuickFilter(param, fkey)}
                  data-testid={`quick-chip-${param}`}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors ${
                    active
                      ? 'border-primary bg-primary text-white'
                      : 'border-gray-200 bg-white text-gray-600 hover:border-primary/50 hover:text-primary'
                  }`}
                >
                  {label}
                </button>
              );
            })}
            <SaveSearchButton filters={filters} />
          </div>
        </div>
        
        {/* Three Images Row - object-top to show faces */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          {JOBS_PAGE_IMAGES.map((img, index) => (
            <div key={index} className="overflow-hidden rounded-xl shadow-md">
              <img 
                src={img}
                alt={`Social work professional ${index + 1}`}
                className="w-full h-32 md:h-40 object-cover object-top hover:scale-105 transition-transform duration-300"
              />
            </div>
          ))}
        </div>

        {/* Search Bar */}
        <form onSubmit={handleSearch} className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 mb-6">
          <div className="flex flex-col lg:flex-row gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                type="text"
                placeholder="Job title, keywords, or company"
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="pl-10 h-11"
                data-testid="search-input"
              />
            </div>
            <div className="flex-1">
              <LocationAutocomplete
                placeholder="City, state, or remote"
                value={filters.location}
                onChange={(value) => setFilters({ ...filters, location: value })}
                className="h-11"
              />
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className="lg:hidden h-11"
            >
              <Filter className="w-4 h-4 mr-2" />
              Filters
            </Button>
            <Button type="submit" className="bg-primary hover:bg-primary-hover text-white h-11 px-6" data-testid="search-btn">
              Search
            </Button>
          </div>
        </form>

        <div className="grid lg:grid-cols-4 gap-8">
          {/* Sidebar Filters */}
          <aside className={`lg:col-span-1 ${showFilters ? 'block' : 'hidden lg:block'}`}>
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 sticky top-24">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-semibold text-gray-900">Filters</h3>
                {hasActiveFilters && (
                  <button
                    onClick={clearFilters}
                    className="text-sm text-primary hover:underline flex items-center gap-1"
                    data-testid="clear-filters-btn"
                  >
                    <X className="w-3 h-3" /> Clear
                  </button>
                )}
              </div>

              {/* License Type */}
              <div className="mb-6">
                <Label className="text-sm font-medium text-gray-700 mb-3 block">
                  License Type
                </Label>
                <Select
                  value={filters.license}
                  onValueChange={(value) => setFilters({ ...filters, license: value })}
                >
                  <SelectTrigger data-testid="license-filter">
                    <SelectValue placeholder="All Licenses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Licenses</SelectItem>
                    <SelectItem value="LCSW">LCSW</SelectItem>
                    <SelectItem value="LMSW">LMSW</SelectItem>
                    <SelectItem value="LBSW">LBSW</SelectItem>
                    <SelectItem value="SWA">Social Work Associate</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Job Type */}
              <div className="mb-6">
                <Label className="text-sm font-medium text-gray-700 mb-3 block">
                  Job Type
                </Label>
                <Select
                  value={filters.jobType}
                  onValueChange={(value) => setFilters({ ...filters, jobType: value })}
                >
                  <SelectTrigger data-testid="job-type-filter">
                    <SelectValue placeholder="All Types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    {jobTypeOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Remote */}
              <div className="mb-6">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="remote"
                    checked={filters.isRemote}
                    onCheckedChange={(checked) => setFilters({ ...filters, isRemote: checked })}
                    data-testid="remote-filter"
                  />
                  <Label htmlFor="remote" className="text-sm text-gray-700 cursor-pointer">
                    Remote positions only
                  </Label>
                </div>
              </div>

              {/* Government Jobs */}
              <div className="mb-6">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="government"
                    checked={filters.isGovernment}
                    onCheckedChange={(checked) => setFilters({ ...filters, isGovernment: checked })}
                    data-testid="government-filter"
                  />
                  <Label htmlFor="government" className="text-sm text-gray-700 cursor-pointer">
                    Government jobs only
                  </Label>
                </div>
              </div>

              {/* Setting Type */}
              <div className="mb-6">
                <Label className="text-sm font-medium text-gray-700 mb-3 block">
                  Setting Type
                </Label>
                <Select
                  value={filters.settingType || 'all'}
                  onValueChange={(value) => setFilters({ ...filters, settingType: value })}
                >
                  <SelectTrigger data-testid="setting-filter">
                    <SelectValue placeholder="All Settings" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Settings</SelectItem>
                    <SelectItem value="hospital">Hospital</SelectItem>
                    <SelectItem value="clinic">Clinic/Outpatient</SelectItem>
                    <SelectItem value="school">School</SelectItem>
                    <SelectItem value="hospice">Hospice</SelectItem>
                    <SelectItem value="community">Community/Agency</SelectItem>
                    <SelectItem value="private">Private Practice</SelectItem>
                    <SelectItem value="government">Government</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Special Populations & Skills — who the social worker serves */}
              <div className="mb-6">
                <Label className="text-sm font-medium text-gray-700 mb-1 block">
                  Special Populations &amp; Skills
                </Label>
                <p className="text-xs text-gray-400 mb-3">Find roles serving a specific community.</p>
                <div className="space-y-2" data-testid="populations-filter">
                  {POPULATIONS.map((p) => (
                    <div key={p.slug} className="flex items-center space-x-2">
                      <Checkbox
                        id={`pop-${p.slug}`}
                        checked={filters.populations?.includes(p.slug)}
                        onCheckedChange={() => togglePopulation(p.slug)}
                        data-testid={`pop-${p.slug}`}
                      />
                      <Label htmlFor={`pop-${p.slug}`} className="text-sm text-gray-700 cursor-pointer leading-tight">
                        {p.label}
                      </Label>
                    </div>
                  ))}
                </div>
                <Link to="/deaf-hard-of-hearing-social-work-jobs" className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-secondary hover:underline" data-testid="deaf-landing-link">
                  Explore Deaf &amp; HoH jobs page →
                </Link>
              </div>

              {/* Technology & Administrative Support (employer-reported) */}
              <div className="mb-6" data-testid="support-filter">
                <Label className="text-sm font-medium text-gray-700 mb-1 block">
                  Technology &amp; Administrative Support
                </Label>
                <p className="text-xs text-gray-400 mb-3">Employer-reported. Candidates should confirm during hiring.</p>
                <div className="space-y-2">
                  {TECH_ADMIN_FILTERS.map((opt) => (
                    <div key={opt} className="flex items-center space-x-2">
                      <Checkbox
                        id={`support-${opt}`}
                        checked={filters.support?.includes(opt)}
                        onCheckedChange={() => toggleSupport(opt)}
                        data-testid={`support-${opt.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}
                      />
                      <Label htmlFor={`support-${opt}`} className="text-sm text-gray-700 cursor-pointer leading-tight">
                        {opt}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Supervision Provided */}
              <div className="mb-6">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="supervision"
                    checked={filters.supervisionProvided}
                    onCheckedChange={(checked) => setFilters({ ...filters, supervisionProvided: checked })}
                    data-testid="supervision-filter"
                  />
                  <Label htmlFor="supervision" className="text-sm text-gray-700 cursor-pointer">
                    Supervision provided
                  </Label>
                </div>
              </div>

              <Button
                onClick={handleSearch}
                className="w-full bg-primary hover:bg-primary-hover text-white"
                data-testid="apply-filters-btn"
              >
                Apply Filters
              </Button>
            </div>
          </aside>

          {/* Job Listings */}
          <main className="lg:col-span-3">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : jobs.length > 0 ? (
              <>
                <div className="space-y-4">
                  {jobs.map((job) => (
                    <JobCard
                      key={job.job_id}
                      job={job}
                      onSave={handleSaveJob}
                      onUnsave={handleUnsaveJob}
                      isSaved={savedJobs.has(job.job_id)}
                    />
                  ))}
                </div>

                {/* Pagination */}
                {pagination.pages > 1 && (
                  <div className="flex justify-center gap-2 mt-8">
                    <Button
                      variant="outline"
                      disabled={pagination.page <= 1}
                      onClick={() => {
                        const params = new URLSearchParams(searchParams);
                        params.set('page', String(pagination.page - 1));
                        setSearchParams(params);
                      }}
                    >
                      Previous
                    </Button>
                    <span className="flex items-center px-4 text-sm text-gray-600">
                      Page {pagination.page} of {pagination.pages}
                    </span>
                    <Button
                      variant="outline"
                      disabled={pagination.page >= pagination.pages}
                      onClick={() => {
                        const params = new URLSearchParams(searchParams);
                        params.set('page', String(pagination.page + 1));
                        setSearchParams(params);
                      }}
                    >
                      Next
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-16 bg-white rounded-xl border border-gray-100">
                <Briefcase className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="font-serif text-xl font-semibold text-gray-900 mb-2">
                  {filters.license === 'SWA' ? 'SWA Jobs Coming Soon!' : 'No jobs found'}
                </h3>
                <p className="text-gray-500 mb-4">
                  {filters.license === 'SWA' 
                    ? 'We\'re actively adding Social Work Associate positions. Check back soon!'
                    : 'Try adjusting your search filters or check back later for new listings.'}
                </p>
                <p className="text-sm text-primary mb-6">
                  📅 New jobs posted daily at 8:00 AM CST
                </p>
                <Button variant="outline" onClick={clearFilters}>
                  Clear Filters
                </Button>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default Jobs;
